import type { InjectionKey } from 'vue'

export type VideoPresetControl =
  | 'play'
  | 'play-prev'
  | 'play-next'
  | 'refresh'
  | 'timer'
  | 'playback-rate'
  | 'flip'
  | 'volume'
  | 'pip'
  | 'full-window'
  | 'full-browser'
export type VideoShortcutOptions = Partial<Record<VideoPresetControl, string>>
// eslint-disable-next-line @typescript-eslint/ban-types
export type VideoControlName = VideoPresetControl | (string & {})
export type VideoControlConfig = VideoControlName | [VideoControlName, any]

export interface VideoControlLayout {
  left?: VideoControlConfig[]
  center?: VideoControlConfig[]
  right?: VideoControlConfig[]
}

export interface VideoPlaybackRate {
  label?: string
  value: number
}

export type VideoControlType = 'button' | 'select' | 'panel'

export interface VideoControlOption {
  value: string | number
  label?: string
  selectedLabel?: string
  disabled?: boolean
  divided?: boolean
  title?: string
}

export interface VideoSegment {
  time: number
  title?: string
}

// export interface VideoKernel {
//   play: () => void,
//   pause: () => void,
//   mute: () => void,
//   unmute: () => void,
//   prev: () => void,
//   next: () => void,
//   refresh: () => void | boolean
// }

export interface VideoState {
  placeId?: string
  iconScale: number
  addShortcut: (key: string, cb: () => void) => () => void
}

const defaultLayout: Required<VideoControlLayout> = {
  left: ['play-prev', 'play', 'play-next', 'refresh', 'timer'],
  center: [],
  right: [
    'playback-rate',
    'volume',
    'flip',
    'pip',
    'full-window',
    'full-browser',
  ],
}

for (const key of Object.keys(defaultLayout) as (keyof VideoControlLayout)[]) {
  defaultLayout[key] = Object.freeze(defaultLayout[key]) as any
}

export const videoDefaultControlLayout = Object.freeze(defaultLayout)

export const videoDefaultShortcuts = Object.freeze({
  play: 'Space',
  'play-prev': 'PageUp',
  'play-next': 'PageDown',
  refresh: 'R',
  flip: 'C',
  volume: 'M',
  pip: 'P',
  'full-window': 'G',
  'full-browser': 'F',
})

export const VIDEO_STATE =
  '__VXP_VIDEO_STATE' as unknown as InjectionKey<VideoState>

export const localeVideo = {
  play: '播放',
  pause: '暂停',
  playPrev: '上一个',
  playNext: '下一个',
  refresh: '刷新',
  flip: '镜像翻转',
  requestPip: '进入画中画',
  exitPip: '退出画中画',
  fullWindow: '进入网页全屏',
  fullWindowExit: '退出网页全屏',
  fullScreen: '进入全屏',
  fullScreenExit: '退出全屏',
  chapterCount: '第 {n} 章',
}

/**
 * 根据一系列判断条件，执行第一个为 `true` 的条件所对应的回调函数
 *
 * @param conditions 判断条件及回调函数
 * @param options 额外的选项
 *
 * @returns 是否匹配了任一条件
 */
export async function decide(
  conditions: [boolean | (() => boolean), () => void | Promise<void>][],
  options: {
    /**
     * 当匹配任意一个条件时，会在该条件对应的回调函数执行前执行
     */
    beforeMatchAny?: () => void | Promise<void>
    /**
     * 当匹配任意一个条件时，会在该条件对应的回调函数执行完后执行
     */
    afterMatchAny?: () => void | Promise<void>
  } = {}
) {
  if (conditions.length) {
    for (const [condition, callback] of conditions) {
      if (typeof condition === 'function' ? condition() : condition) {
        if (typeof options.beforeMatchAny === 'function') {
          await options.beforeMatchAny()
        }

        await callback()

        if (typeof options.afterMatchAny === 'function') {
          await options.afterMatchAny()
        }

        return true
      }
    }
  }

  return false
}
